"use client";

import { focusManager } from "@tanstack/react-query";
import { useEffect } from "react";
import log from "logbeacon";
import { getBridge } from "@/src/lib/bridge/bridge";
import {
  pauseNotificationSnapshotPollingForNativeTabFreeze,
  resumeNotificationSnapshotPollingForNativeTabFreeze,
} from "@/src/features/notification/sync/supabaseNotificationSyncCoordinator";
import { useOptionalWebSocketNotificationContext } from "@/src/features/notification/WebSocketNotificationContext";

/** 与 iOS/Android `BridgeBus.freezeTab` 注入的全局标志对齐 */
export const NATIVE_TAB_FROZEN_KEY = "__NATIVE_TAB_FROZEN__";

/** 供其它模块（如图表、动画）订阅 freeze/resume */
export const NATIVE_TAB_FREEZE_CHANGE_EVENT = "native-tab-freeze-change";

export type NativeTabFreezeChangeDetail = {
  frozen: boolean;
};

declare global {
  interface Window {
    __NATIVE_TAB_FROZEN__?: boolean;
  }
}

let installed = false;
let currentFrozen = false;

export function isNativeTabFrozen(): boolean {
  if (typeof window === "undefined") {
    return false;
  }
  return window.__NATIVE_TAB_FROZEN__ === true;
}

function dispatchNativeTabFreezeChange(frozen: boolean): void {
  if (typeof window === "undefined") {
    return;
  }
  window.dispatchEvent(
    new CustomEvent<NativeTabFreezeChangeDetail>(
      NATIVE_TAB_FREEZE_CHANGE_EVENT,
      { detail: { frozen } },
    ),
  );
}

type NativeTabFreezeSideEffects = {
  onFrozen?: () => void;
  onResumed?: () => void;
};

function applyNativeTabFrozenState(
  frozen: boolean,
  sideEffects?: NativeTabFreezeSideEffects,
): void {
  if (frozen === currentFrozen) {
    return;
  }
  currentFrozen = frozen;

  focusManager.setFocused(!frozen);

  if (frozen) {
    pauseNotificationSnapshotPollingForNativeTabFreeze();
    sideEffects?.onFrozen?.();
  } else {
    resumeNotificationSnapshotPollingForNativeTabFreeze();
    sideEffects?.onResumed?.();
  }

  dispatchNativeTabFreezeChange(frozen);

  log.debug("[NativeTabFreezeSync] state", { frozen });
}

/**
 * 监听原生 `window.__NATIVE_TAB_FROZEN__` 注入，统一 pause/resume Web 侧后台开销。
 * 返回 cleanup，供单例 hook 在 Strict Mode 下安全卸载。
 */
export function installNativeTabFrozenListener(
  sideEffects?: NativeTabFreezeSideEffects,
): () => void {
  if (typeof window === "undefined" || installed) {
    return () => {};
  }
  installed = true;

  const initialFrozen = window.__NATIVE_TAB_FROZEN__ === true;

  try {
    Object.defineProperty(window, NATIVE_TAB_FROZEN_KEY, {
      configurable: true,
      enumerable: true,
      get() {
        return currentFrozen;
      },
      set(value: unknown) {
        applyNativeTabFrozenState(value === true, sideEffects);
      },
    });
  } catch (error) {
    log.warn(
      "[NativeTabFreezeSync] defineProperty failed, fallback to polling",
      {
        error,
      },
    );
    let pollFrozen = window.__NATIVE_TAB_FROZEN__ === true;
    const pollTimer = window.setInterval(() => {
      const next = window.__NATIVE_TAB_FROZEN__ === true;
      if (next !== pollFrozen) {
        pollFrozen = next;
        applyNativeTabFrozenState(next, sideEffects);
      }
    }, 500);

    applyNativeTabFrozenState(pollFrozen, sideEffects);

    return () => {
      window.clearInterval(pollTimer);
      installed = false;
    };
  }

  applyNativeTabFrozenState(initialFrozen, sideEffects);

  return () => {
    installed = false;
  };
}

/**
 * App 多 Tab 壳：非活跃 WebView 被原生 freeze 时，暂停 React Query focus refetch、
 * Supabase 15s snapshot 轮询与 WS 连接；resume 时恢复。
 */
export function useNativeTabFreezeSync(): void {
  const notificationContext = useOptionalWebSocketNotificationContext();
  const connect = notificationContext?.connect;
  const disconnect = notificationContext?.disconnect;

  useEffect(() => {
    if (!getBridge().isInApp()) {
      return;
    }

    return installNativeTabFrozenListener({
      onFrozen: () => {
        disconnect?.();
      },
      onResumed: () => {
        connect?.();
      },
    });
  }, [connect, disconnect]);
}
